import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { useSite } from "@/context/SiteContext";

export const LegalLayout = ({ title, lastUpdated, intro = [], sections = [], testid, description, es = false, altPath, seoPath, alternates }) => {
  const { openBooking, lang, langTick, changeLang } = useSite();
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  // Language switcher keeps the visitor on the same page in the other language.
  // Only reacts to an explicit toggle click AFTER mount (langTick) — a stored
  // preference never redirects a direct visit (crawlers/shared links see this page).
  const prevLang = React.useRef(langTick);
  useEffect(() => {
    if (prevLang.current === langTick) return;
    prevLang.current = langTick;
    if (altPath && (lang === "es") !== es) navigate(altPath, { replace: true });
  }, [lang, langTick, es, altPath, navigate]);

  return (
    <div className="bg-offwhite min-h-screen pb-16 md:pb-0" data-testid={testid}>
      <Seo title={title} description={description} path={seoPath} alternates={alternates} />
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-navy/5">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Revive" className="h-9 w-auto" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl text-gold">Revive</span>
              <span className="font-sans text-[9px] tracking-[0.22em] uppercase text-navy/55 mt-1">Regenerative Medical Clinic</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {altPath && (
              <div className="flex items-center rounded-full border border-navy/15 overflow-hidden" data-testid="legal-lang-toggle">
                <button onClick={() => changeLang("en")} className={`px-3.5 py-1.5 text-xs font-sans tracking-wide transition-colors ${!es ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`} data-testid="legal-lang-en">EN</button>
                <button onClick={() => changeLang("es")} className={`px-3.5 py-1.5 text-xs font-sans tracking-wide transition-colors ${es ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`} data-testid="legal-lang-es">ES</button>
              </div>
            )}
            <button onClick={() => openBooking()} className="rounded-full bg-navy text-white text-sm px-5 py-2.5 hover:bg-royal transition-colors">
              {es ? "Solicitar una consulta" : "Request a Consultation"}
            </button>
          </div>
        </div>
      </header>

      <div className="bg-navy text-white">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 pt-16 pb-14">
          <Link to="/" className="inline-flex items-center gap-2 text-champagne/80 hover:text-champagne text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> {es ? "Volver al inicio" : "Back to home"}
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{title}</h1>
          {lastUpdated && <p className="font-sans text-white/50 text-sm mt-4">{es ? "Última actualización" : "Last Updated"}: {lastUpdated}</p>}
        </div>
      </div>

      <main className="max-w-[1000px] mx-auto px-6 sm:px-10 py-14">
        {intro.map((para, i) => (
          <p key={i} className="font-sans text-charcoal/75 leading-relaxed mt-4 first:mt-0">{para}</p>
        ))}
        <div className="mt-12 space-y-11">
          {sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">{s.h}</h2>
              {s.p?.map((para, i) => (
                <p key={i} className="font-sans text-charcoal/75 leading-relaxed mt-4">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((li) => (
                    <li key={li} className="flex items-start gap-3 font-sans text-charcoal/75">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after?.map((para, i) => (
                <p key={i} className="font-sans text-charcoal/60 italic leading-relaxed mt-4 border-l-2 border-champagne pl-4">{para}</p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalLayout;
